import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

// i18n do painel. Dicionários em i18n/locales/<idioma>/<namespace>.json ({ "chave": "texto" }).
// Chave usada no código = "<namespace>.<chave>" (ex.: t('reviews.title')).
// Cada idioma vira UM chunk (i18n/locales/<idioma>.ts) carregado sob demanda: fora do bundle
// principal (budget de 60 kB). pt-BR sempre carrega — é o fallback.
// Interpolação: t('x.y', { n: 3 }) troca {n} no texto.
export type Locale = 'pt-BR' | 'en' | 'es';
export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'pt-BR', label: 'Português' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];
const STORAGE_KEY = 'rsvs.locale';

const LOADERS: Record<Locale, () => Promise<{ default: Record<string, string> }>> = {
  'pt-BR': () => import('./locales/pt-BR'),
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
};
const DICTS: Partial<Record<Locale, Record<string, string>>> = {};

async function ensureLoaded(locale: Locale): Promise<void> {
  const needed = locale === 'pt-BR' ? ['pt-BR' as const] : (['pt-BR', locale] as Locale[]);
  await Promise.all(needed.filter((l) => !DICTS[l]).map(async (l) => { DICTS[l] = (await LOADERS[l]()).default; }));
}

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'pt-BR' || saved === 'en' || saved === 'es') return saved;
  } catch { /* storage bloqueado: segue o padrão */ }
  return 'pt-BR';
}

// Locale ativo também fora de componentes (formatadores de data/moeda). Atualizado de forma
// síncrona no setLocale, ANTES do setState, para que o re-render já formate no idioma novo.
let activeLocale: Locale = initialLocale();
export function currentLocale(): Locale {
  return activeLocale;
}

const Ctx = createContext<{ locale: Locale; setLocale: (l: Locale) => void }>({ locale: 'pt-BR', setLocale: () => {} });

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => activeLocale);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    ensureLoaded(locale).then(() => setReady(true)).catch((e: Error) => setLoadError(e));
  }, [locale]);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    // Troca só depois do chunk do idioma chegar: nunca renderiza chave crua.
    ensureLoaded(l).then(() => {
      activeLocale = l;
      setLocaleState(l);
      try { localStorage.setItem(STORAGE_KEY, l); } catch { /* idem */ }
    }).catch((e: Error) => setLoadError(e));
  }, []);

  // Provider fica fora do ErrorBoundary (main.tsx): mostra a falha real aqui mesmo.
  if (loadError) return <p role="alert" style={{ padding: 24 }}>Falha ao carregar o idioma: {loadError.message}</p>;
  if (!ready) return null;
  return <Ctx.Provider value={{ locale, setLocale }}>{children}</Ctx.Provider>;
}

export function useLocale() {
  return useContext(Ctx);
}

// Chave sem tradução no idioma ativo cai no pt-BR; sem pt-BR, mostra a própria chave
// (aparece na tela e no teste de chaves — nunca vira texto vazio).
export function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const raw = DICTS[locale]?.[key] ?? DICTS['pt-BR']?.[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m)) : raw;
}

export function useT() {
  const { locale } = useContext(Ctx);
  return useCallback((key: string, vars?: Record<string, string | number>) => translate(locale, key, vars), [locale]);
}
